// KPI tile. V-shape-native: resolves the `:value` (and
// optional `:delta`) `ColumnRef`s to columns, takes the latest value
// (`rows.at(-1)` semantics), and delegates
// rendering to the existing `StandardKpi` leaf unchanged.

import { type ReactNode, useId } from 'react';

import {
  formatCompact,
  formatCurrency,
  formatInteger,
  formatPercent,
} from '#core/components/dashboard/number-format.ts';
import { StandardKpi } from '#core/components/dashboard/panels/kpi/variants/StandardKpi.tsx';
import { NO_POS } from '#core/lib/expr/ast.ts';
import type { MetricFormat } from '#core/lib/expr/ops/dashboard/enums.ts';
import { metricScalarError } from '#core/lib/expr/ops/dashboard/validate.ts';
import type { MetricSpec } from '#core/lib/expr/ops/dashboard/value.ts';
import { formatDisplayInstant } from '#core/lib/expr/ops/transform/date-utils.ts';
import { numberCol, unitOfType, withUnit } from '#core/lib/expr/schema/types.ts';
import type { Cell } from '#core/lib/expr/value.ts';
import { cellNumber, cellScalar, numericScalarOf, unitOfCell } from '#core/lib/expr/value.ts';
import { slugify } from '#core/lib/utils/slugify.ts';

import { ladderedUnitDisplay, unitLabel } from '../unit-label.ts';
import { EMPTY } from './cell.ts';
import { TileFrame } from './TileFrame.tsx';
import { useResolvedColumns } from './useResolvedColumns.ts';

/**
 * Per-format numeric renderers. `number` is intentionally absent — it is the
 * default branch, handing the raw number to StandardKpi, which formats with
 * thousands separators via Mantine's NumberFormatter. The `satisfies` clause
 * keeps the table exhaustive over every non-default `MetricFormat`.
 */
const METRIC_FORMATTERS: Partial<Record<MetricFormat, (n: number) => ReactNode>> = {
  compact: formatCompact,
  integer: formatInteger,
  percent: formatPercent,
  currency: formatCurrency,
} satisfies Record<Exclude<MetricFormat, 'number'>, (n: number) => ReactNode>;

/** Apply the tile's `:format` to the latest cell value. */
function formatMetric(value: Cell | undefined, format: MetricFormat): ReactNode {
  if (typeof value !== 'number') {
    // Non-numeric (string label, null, nested Dataset) — render as-is
    // / blank; StandardKpi handles strings verbatim.
    if (value == null || typeof value === 'object') {
      return EMPTY;
    }
    return String(value);
  }
  const formatter = METRIC_FORMATTERS[format];
  return formatter ? formatter(value) : value;
}

/**
 * Percent change of the delta column's last two points → a
 * StandardKpi trend indicator. Returns `{}` when there isn't enough
 * finite data to compute a change.
 */
function computeTrend(deltaCol: readonly Cell[]): { trend?: 'up' | 'down' | 'neutral'; trendValue?: number } {
  const latest = cellNumber(deltaCol.at(-1));
  const prev = cellNumber(deltaCol.at(-2));
  if (!Number.isFinite(latest) || !Number.isFinite(prev) || prev === 0) {
    return {};
  }
  const trendValue = ((latest - prev) / Math.abs(prev)) * 100;
  if (trendValue > 0) {
    return { trend: 'up', trendValue };
  }
  if (trendValue < 0) {
    return { trend: 'down', trendValue };
  }
  return { trend: 'neutral', trendValue };
}

export function MetricPanel({ spec }: { spec: MetricSpec }) {
  const tileId = useId();
  // Stable, positionally-indexed ref list so the resolved-column
  // indices don't shift as optional refs come and go. Order is fixed:
  // value, then delta (if any), then as-of (if any).
  const refs = [
    spec.value,
    ...(spec.delta === undefined ? [] : [spec.delta]),
    ...(spec.asOf === undefined ? [] : [spec.asOf]),
  ];
  const deltaIdx = spec.delta === undefined ? -1 : 1;
  const asOfIdx = spec.asOf === undefined ? -1 : refs.length - 1;
  const cols = useResolvedColumns(refs);

  const valueCol = cols[0] ?? [];
  const tileName = `metric-${slugify(spec.label)}`;
  // A metric must be a single scalar — silently taking `.at(-1)` of a
  // multi-row column hides a misconfigured `:value` ref. SSOT predicate
  // shared with execute-time `validateDashboardSpec`: the agent flow
  // throws this same error before claiming success; here (render, the
  // whole grid is under one ErrorBoundary) we degrade gracefully.
  const scalarErr = metricScalarError(spec, valueCol, NO_POS);
  if (scalarErr) {
    return (
      <TileFrame id={tileId} inspect={() => ({ spec, error: scalarErr.message })} name={tileName}>
        <StandardKpi label={scalarErr.message} value="⚠ error" />
      </TileFrame>
    );
  }

  const declaredType = spec.value.ds.schema.get(spec.value.column);
  // `percent`/`currency` formats inject their own `%`/`$` symbol, so they win
  // outright — no declared unit and no ladder scaling (a percent is already a
  // dimensionless display). Everything else may carry a unit.
  const ownSymbolFormat = spec.format === 'percent' || spec.format === 'currency';
  // Auto mode: a standalone KPI fixes its rung from its own latest value (the
  // only value it shows), folding magnitude into the petroleum unit prefix.
  // A unit-boxed cell collapses to its bare number for formatting; its box
  // supplies the display unit when the column type carries none (units are
  // value-level truth — the schema often has no column-level unit).
  const latestCell = valueCol.at(-1);
  const latest = numericScalarOf(latestCell) ?? latestCell;
  const declaredUnit = declaredType === undefined ? undefined : unitOfType(declaredType);
  const boxedUnit = unitOfCell(latestCell);
  const valueType =
    declaredUnit === undefined && boxedUnit !== undefined ? withUnit(numberCol, boxedUnit) : declaredType;
  const laddered = ownSymbolFormat || typeof latest !== 'number' ? undefined : ladderedUnitDisplay(valueType, [latest]);
  const value = laddered ? laddered.format(latest as number) : formatMetric(latest, spec.format);
  // Declared unit shown beside the value — the laddered label (e.g. MMSCF/d)
  // when the dimension ladders, else the magnitude-independent symbol.
  const unit = ownSymbolFormat ? undefined : (laddered?.unit ?? unitLabel(valueType)) || undefined;
  const { trend, trendValue } = spec.delta === undefined ? {} : computeTrend(cols[deltaIdx] ?? []);

  const asOfCell = spec.asOf === undefined ? undefined : cols[asOfIdx]?.at(-1);
  const asOfText = asOfCell === undefined ? null : formatDisplayInstant(cellScalar(asOfCell));
  // `StandardKpi` has no subtitle/secondary slot and is out of scope to
  // modify — fold the "as of <date>" into the free-text `label`.
  const label = asOfText === null ? spec.label : `${spec.label} (as of ${asOfText})`;

  return (
    <TileFrame id={tileId} inspect={() => ({ spec, cols })} name={tileName}>
      <StandardKpi label={label} trend={trend} trendValue={trendValue} unit={unit} value={value} />
    </TileFrame>
  );
}
